<template>
  <div class="container p-0" v-show="!hidden">
    <div class="input-group geo-invoer">
      <input
        :ref="'inputField_' + element.name"
        type="text"
        class="form-control geo-clickable-input"
        :placeholder="hasLocation ? '' : getTextFor(element.placeholder, lang)"
        :value="hasLocation ? locationData.address : ''"
        readonly
        :class="{ 'is-invalid': isInputInvalid, 'is-valid': hasLocation && isLocationConfirmed && !hasLocationConflict }"
        :aria-describedby="describedBy"
        @click="openMapModal"
      />
      <!--
        Validatieveld. Stond op type="hidden", en die zijn volgens de HTML-spec
        uitgesloten van constraint-validatie: die `required` deed dus niets.
        Gevolg: de browser liet verzenden toe zonder locatie en pas de server
        wees het af, met een melding onderaan in plaats van bij het veld.

        Een readonly veld is óók uitgesloten, dus het moet een gewoon tekstveld
        zijn dat buiten beeld staat. aria-hidden en tabindex=-1 houden het uit
        de schermlezer en uit de tabvolgorde; data-veldnaam en data-focus-voor
        vertellen de foutensamenvatting hoe het veld heet en waar de focus heen
        moet.
      -->
      <input
        :ref="'validationField_' + element.name"
        type="text"
        class="geo-validatie"
        :id="ids && ids.input"
        :name="element.name"
        :value="hasLocation ? locationData.address || '1' : ''"
        :required="element.required"
        :data-veldnaam="element.label && element.label[lang]"
        :data-focus-voor="ids && ids.field + '-knop'"
        tabindex="-1"
        aria-hidden="true"
        @input="() => {}"
      />
      <button
        class="btn btn-primary"
        type="button"
        :id="ids && ids.field + '-knop'"
        @click="openMapModal"
      >
        {{ hasLocation ? 'Wijzigen' : 'Selecteer locatie' }}
      </button>
    </div>
    <div class="invalid-feedback" :style="invalidStyle">
      {{
        hasLocationConflict 
          ? "Locatie conflict: geo component en gekoppelde velden verschillen" 
          : (getTextFor(element.invalid, lang) || "Selecteer een locatie op de kaart")
      }}
    </div>
    <div
      v-if="showFormTexts && element.explanation"
      :id="ids && ids.help"
      class="form-text"
    >
      {{ getTextFor(element.explanation, lang) }}
    </div>

    <!-- Mini map preview -->
    <div v-if="hasLocation" class="mt-2 geo-preview-container" :class="{ 'conflict-border': hasLocationConflict || !isLocationConfirmed }">
      <div id="miniMap" class="geo-mini-map" ref="miniMapContainer"></div>
      <div class="geo-coordinates">
        <span
          >Lat: {{ locationData.lat.toFixed(6) }}, Lng:
          {{ locationData.lng.toFixed(6) }}</span
        >
        <button
          class="btn btn-sm btn-outline-danger ms-2"
          @click="clearLocation"
        >
          Wissen
        </button>
      </div>
    </div>
    
    <!-- Conflict warning -->
    <div v-if="hasLocationConflict" class="alert alert-warning mt-2 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      <div>
        <strong>Locatie conflict gedetecteerd!</strong><br>
        <small>De geo component locatie verschilt van de gekoppelde latitude/longitude velden. Open de kaart om te synchroniseren.</small>
      </div>
    </div>
    
    <!-- Confirmation warning -->
    <div v-if="hasLocation && !isLocationConfirmed && !hasLocationConflict" class="alert alert-info mt-2 d-flex align-items-center">
      <i class="bi bi-info-circle-fill me-2"></i>
      <div>
        <strong>Locatie moet bevestigd worden!</strong><br>
        <small>Deze locatie is overgenomen van gekoppelde velden. Open de kaart en bevestig de locatie.</small>
      </div>
    </div>

    <!-- Modal voor de grote kaart -->
    <div
      class="modal fade"
      id="mapModal"
      tabindex="-1"
      aria-labelledby="mapModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="mapModalLabel">
              Kies een locatie op de kaart
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <div class="input-group mb-3 position-relative">
              <input
                type="text"
                class="form-control"
                v-model="modalAddressInput"
                placeholder="Zoek locatie"
                name="geo-search-input"
                autocomplete="new-password"
                autocorrect="off"
                autocapitalize="off"
                spellcheck="false"
                @input="onModalAddressInput"
                @keydown.enter="handleModalEnter"
                @keydown.arrow-down="navigateDropdown(1)"
                @keydown.arrow-up="navigateDropdown(-1)"
                @keydown.escape="hideModalDropdown"
                @blur="hideModalDropdownDelayed"
              />
              <button
                class="btn btn-outline-secondary"
                type="button"
                @click="searchAddressInModal"
              >
                Zoeken
              </button>
              
              <!-- Dropdown voor modal input -->
              <div v-if="showModalDropdown && modalSuggestions.length" class="autocomplete-dropdown">
                <div 
                  v-for="(suggestion, index) in modalSuggestions"
                  :key="suggestion.place_id"
                  @mousedown="selectModalSuggestion(suggestion)"
                  @mouseenter="modalSelectedIndex = index"
                  :class="['autocomplete-item', { active: index === modalSelectedIndex }]"
                >
                  <strong>{{ suggestion.structured_formatting?.main_text || suggestion.description }}</strong>
                  <div class="text-muted small">{{ suggestion.structured_formatting?.secondary_text }}</div>
                </div>
              </div>
            </div>
            <div class="form-text mb-2">
              Klik op de kaart om een locatie te selecteren of zoek op adres
            </div>
            <div id="fullMap" class="geo-full-map" ref="fullMapContainer"></div>

            <div class="mt-3">
              <h6>Geselecteerde Coördinaten</h6>
              <div class="row">
                <div class="col-md-6">
                  <div class="input-group mb-3">
                    <span class="input-group-text">Lat</span>
                    <input
                      type="number"
                      class="form-control"
                      :class="{ 'is-invalid': isModalLatInvalid }"
                      v-model="tempLocation.lat"
                      step="0.000001"
                    />
                  </div>
                  <div v-if="isModalLatInvalid" class="small text-danger">
                    Verschilt met gekoppeld veld {{ element.latField }}: {{ mappedFieldsLocation.lat.toFixed(7) }}
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="input-group mb-3">
                    <span class="input-group-text">Lng</span>
                    <input
                      type="number"
                      class="form-control"
                      :class="{ 'is-invalid': isModalLngInvalid }"
                      v-model="tempLocation.lng"
                      step="0.000001"
                    />
                  </div>
                  <div v-if="isModalLngInvalid" class="small text-danger">
                    Verschilt met gekoppeld veld {{ element.lngField }}: {{ mappedFieldsLocation.lng.toFixed(7) }}
                  </div>
                </div>
              </div>


            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="cancelMapModal"
            >
              Annuleren
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="confirmLocation"
            >
              Bevestigen
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* global google, bootstrap */

export default {
  name: "FormElementGeo",
  props: [
    "element",
    "ids",
    "lang",
    "targetId",
    "wasValidated",
    "formData",
    "hidden",
    "showFormTexts",
  ],
  emits: ["elementChanged"],
  data() {
    return {
      invalidStyle: {},
      modalAddressInput: "",
      locationData: {
        lat: 52.379189,
        lng: 4.899431,
        address: "",
        components: {}
      },
      tempLocation: {
        lat: 52.379189,
        lng: 4.899431,
        address: "",
        components: {}
      },
      hasLocation: false,
      isLocationConfirmed: false,
      miniMap: null,
      fullMap: null,
      miniMarker: null,
      fullMarker: null,
      mapModal: null,
      isInputInvalid: false,
      // Voor mapping velden
      mappedFieldsLocation: null,
      mappedFieldsMarker: null,
      isInitializingMiniMap: false,
      hasLocationConflict: false,
      markerSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="44" viewBox="-16 -22 32 44">
                   <defs>
                     <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                       <feDropShadow dx="2" dy="3" stdDeviation="2" flood-color="rgba(0,0,0,0.3)"/>
                     </filter>
                   </defs>
                   <path d="M 0,-10 C -5.5,-10 -10,-5.5 -10,0 C -10,3 -8,8 -5,10 C -2,12 0,17 0,17 C 0,17 2,12 5,10 C 8,8 10,3 10,0 C 10,-5.5 5.5,-10 0,-10 Z" 
                         fill="#0F6C65" stroke="#FFFFFF" stroke-width="2" filter="url(#shadow)"/>
                   <circle cx="0" cy="0" r="4" fill="#FFFFFF"/>
                 </svg>`,
      redMarkerSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="44" viewBox="-16 -22 32 44">
                      <defs>
                        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                          <feDropShadow dx="2" dy="3" stdDeviation="2" flood-color="rgba(0,0,0,0.3)"/>
                        </filter>
                      </defs>
                      <path d="M 0,-10 C -5.5,-10 -10,-5.5 -10,0 C -10,3 -8,8 -5,10 C -2,12 0,17 0,17 C 0,17 2,12 5,10 C 8,8 10,3 10,0 C 10,-5.5 5.5,-10 0,-10 Z" 
                            fill="#DC3545" stroke="#FFFFFF" stroke-width="2" filter="url(#shadow)"/>
                      <circle cx="0" cy="0" r="4" fill="#FFFFFF"/>
                    </svg>`,
      // Autocomplete data (alleen voor modal)
      autocompleteService: null,
      modalSuggestions: [],
      showModalDropdown: false,
      modalSelectedIndex: -1,
      searchTimeout: null,
      reverseGeocodeTimeout: null,
    };
  },
  computed: {
    /** Verwijzing naar de uitleg onder het veld, met een uniek id per veld. */
    describedBy() {
      const uitleg = this.element && this.element.explanation;
      const heeftUitleg = !!(this.showFormTexts && uitleg && uitleg[this.lang]);
      return heeftUitleg && this.ids ? this.ids.help : undefined;
    },

    // Check of de modal lat input invalid is
    isModalLatInvalid() {
      return this.hasLocationConflict && this.mappedFieldsLocation && 
             Math.abs(this.tempLocation.lat - this.mappedFieldsLocation.lat) > 0.0001;
    },
    
    // Check of de modal lng input invalid is
    isModalLngInvalid() {
      return this.hasLocationConflict && this.mappedFieldsLocation &&
             Math.abs(this.tempLocation.lng - this.mappedFieldsLocation.lng) > 0.0001;
    }
  },
  watch: {
    wasValidated(value) {
      if (!value) {
        this.invalidStyle = {};
        this.isInputInvalid = false;
      } else {
        // Check eerst voor conflict
        this.detectLocationConflict();
        
        const validationElement = this.$refs["validationField_" + this.element.name];
        
        const isValid = this.checkFieldValidity("validationField_" + this.element.name);
        
        this.invalidStyle = {
          display: isValid ? "none" : "block",
        };

        // Voeg validatie-controle toe
        this.isInputInvalid = (this.element.required && (!this.hasLocation || !this.isLocationConfirmed)) || this.hasLocationConflict;

        // Als het veld required is, maar geen locatie heeft of niet bevestigd is, zet de custom validity
        if (validationElement) {
          let customMessage = "";
          if (this.element.required && !this.hasLocation) {
            customMessage = "Selecteer een locatie op de kaart";
          } else if (this.element.required && !this.isLocationConfirmed) {
            customMessage = "Bevestig de locatie in de kaart modal";
          } else if (this.hasLocationConflict) {
            customMessage = "Locatie conflict: geo component en gekoppelde velden verschillen";
          }
          
          validationElement.setCustomValidity(customMessage);
        }
      }
    },
    formData: {
      handler(newVal, oldVal) {
        // Detecteer record wissel door ID vergelijking of nieuwe/lege records
        const isRecordSwitch = this.detectRecordSwitch(newVal, oldVal);
        
        if (isRecordSwitch) {
          console.log('AFormElementGeo: Record switch detected - resetting geo state');
          this.resetGeoState();
        }
        
        // Bestaande geo component synchronisatie
        if (newVal && newVal[this.element.name]) {
          const geoData = newVal[this.element.name];
          if (geoData && geoData.lat && geoData.lng) {
            // Controleer of de waarde daadwerkelijk veranderd is om dubbele updates te voorkomen
            const newLat = parseFloat(geoData.lat);
            const newLng = parseFloat(geoData.lng);

            const hasChanged =
              !this.hasLocation ||
              Math.abs(this.locationData.lat - newLat) > 0.0000001 ||
              Math.abs(this.locationData.lng - newLng) > 0.0000001;

            if (hasChanged) {
              this.locationData = {
                lat: newLat,
                lng: newLng,
                address: geoData.address || '',
              };
              this.hasLocation = true;

              setTimeout(() => {
                this.updateMiniMap();
              }, 100);
            }
          }
        }
        
        // Synchronisatie met gekoppelde velden
        if (newVal && this.element.mapToFields) {
          this.syncWithMappedFields();
        }
      },
      deep: true,
      immediate: true,
    },
    'tempLocation.lat'() {
      this.reverseGeocode();
      // Trigger conflict detectie als er mapping is
      if (this.element.mapToFields) {
        this.detectLocationConflict();
      }
    },
    'tempLocation.lng'() {
      this.reverseGeocode();
      // Trigger conflict detectie als er mapping is  
      if (this.element.mapToFields) {
        this.detectLocationConflict();
      }
    },
  },
  methods: {
    getTextFor(data, lang) {
      return data ? data[lang] : "";
    },
    checkFieldValidity(fieldRef) {
      // Voor geo componenten controleren we het validation field
      const validationField = this.$refs['validationField_' + this.element.name];
      
      if (validationField) {
        const isValid = validationField.checkValidity();
        return isValid;
      }
      
      const field = this.$refs[fieldRef];
      const isValid = field && field.checkValidity();
      return isValid;
    },

    async initMiniMap() {
      try {
        if (this.miniMap && this.miniMarker) {
          this.miniMap.setCenter(this.locationData);
          this.miniMarker.setPosition(this.locationData);
          google.maps.event.trigger(this.miniMap, "resize");
          return;
        }

        if (!this.miniMap && this.$refs.miniMapContainer) {
          this.miniMap = new google.maps.Map(this.$refs.miniMapContainer, {
            zoom: 14,
            center: this.locationData,
            disableDefaultUI: true,
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          });

          await new Promise((resolve) => setTimeout(resolve, 100));
          google.maps.event.trigger(this.miniMap, "resize");

          this.miniMarker = new google.maps.Marker({
            position: this.locationData,
            map: this.miniMap,
            title: "Geselecteerde locatie",
            icon: {
              url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(this.markerSvg),
              size: new google.maps.Size(32, 44),
              anchor: new google.maps.Point(16, 39),
              scaledSize: new google.maps.Size(32, 44),
            },
          });
          
        } else if (this.miniMap) {
          this.miniMap.setCenter(this.locationData);
          google.maps.event.trigger(this.miniMap, "resize");

          if (this.miniMarker) {
            this.miniMarker.setMap(null);
          }

          this.miniMarker = new google.maps.Marker({
            position: this.locationData,
            map: this.miniMap,
            title: "Geselecteerde locatie",
            icon: {
              url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(this.markerSvg),
              size: new google.maps.Size(32, 44),
              anchor: new google.maps.Point(16, 39),
              scaledSize: new google.maps.Size(32, 44),
            },
          });
        }
      } catch (error) {
        alert("De kaart kon niet worden geladen.");
        throw error;
      }
    },
    async initFullMap() {
      try {
        if (!this.fullMap && this.$refs.fullMapContainer) {
          this.tempLocation = {
            lat: this.locationData.lat,
            lng: this.locationData.lng,
          };

          this.fullMap = new google.maps.Map(this.$refs.fullMapContainer, {
            zoom: 10,
            center: this.tempLocation,
            streetViewControl: true,
            mapTypeControl: true,
            clickableIcons: true,
            gestureHandling: "greedy",
          });

          this.$refs.fullMapContainer.style.cursor = "pointer";

          this.fullMarker = new google.maps.Marker({
            position: this.tempLocation,
            map: this.fullMap,
            title: "Geselecteerde locatie",
            draggable: true,
            icon: {
              url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(this.markerSvg),
              size: new google.maps.Size(32, 44),
              anchor: new google.maps.Point(16, 39),
              scaledSize: new google.maps.Size(32, 44),
            },
          });

          this.fullMarker.addListener("dragend", () => {
            this.tempLocation = {
              lat: parseFloat(Number(this.fullMarker.getPosition().lat()).toFixed(7)),
              lng: parseFloat(Number(this.fullMarker.getPosition().lng()).toFixed(7)),
            };
          });

          this.fullMap.addListener("click", (e) => {
            this.tempLocation = {
              lat: parseFloat(Number(e.latLng.lat()).toFixed(7)),
              lng: parseFloat(Number(e.latLng.lng()).toFixed(7)),
            };
            this.fullMarker.setPosition(this.tempLocation);
          });

          // Laad mapped fields marker als aanwezig
          this.syncWithMappedFields();
          this.updateFullMapMarkers();

        } else if (this.fullMap) {
          this.tempLocation = {
            lat: Number(this.locationData.lat),
            lng: Number(this.locationData.lng),
          };
          this.fullMap.setCenter(this.tempLocation);

          if (this.fullMarker) {
            this.fullMarker.setMap(null);
          }

          this.fullMarker = new google.maps.Marker({
            position: this.tempLocation,
            map: this.fullMap,
            title: "Geselecteerde locatie",
            draggable: true,
            icon: {
              url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(this.markerSvg),
              size: new google.maps.Size(32, 44),
              anchor: new google.maps.Point(16, 39),
              scaledSize: new google.maps.Size(32, 44),
            },
          });

          this.fullMarker.addListener("dragend", () => {
            this.tempLocation = {
              lat: parseFloat(Number(this.fullMarker.getPosition().lat()).toFixed(7)),
              lng: parseFloat(Number(this.fullMarker.getPosition().lng()).toFixed(7)),
            };
          });

          google.maps.event.clearListeners(this.fullMap, "click");
          this.fullMap.addListener("click", (e) => {
            this.tempLocation = {
              lat: parseFloat(Number(e.latLng.lat()).toFixed(7)),
              lng: parseFloat(Number(e.latLng.lng()).toFixed(7)),
            };
            this.fullMarker.setPosition(this.tempLocation);
          });

          // Laad mapped fields marker als aanwezig
          this.syncWithMappedFields();
          this.updateFullMapMarkers();
        }
      } catch (error) {
        alert("De kaart kon niet worden geladen.");
      }
    },
    updateMiniMap() {
      if (this.hasLocation && !this.isInitializingMiniMap) {
        this.isInitializingMiniMap = true;

        this.$nextTick(() => {
          this.initMiniMap()
            .then(() => {
              if (this.miniMap) {
                google.maps.event.trigger(this.miniMap, "resize");
                this.miniMap.setCenter(this.locationData);
              }
              this.isInitializingMiniMap = false;
            })
            .catch(() => {
              this.isInitializingMiniMap = false;
            });
        });
      }
    },
    openMapModal() {
      if (!this.mapModal) {
        try {
          if (typeof bootstrap !== "undefined" && bootstrap.Modal) {
            const modalEl = document.getElementById("mapModal");
            if (modalEl) {
              this.mapModal = new bootstrap.Modal(modalEl);
              
              // Voeg event listener toe voor modal verbergen
              modalEl.addEventListener('hidden.bs.modal', () => {
                this.handleModalClose();
              });
            } else {
              throw new Error("Modal element niet gevonden");
            }
          } else {
            throw new Error("Bootstrap Modal niet beschikbaar");
          }
        } catch (error) {
          if (window.jQuery && window.jQuery("#mapModal").modal) {
            window.jQuery("#mapModal").modal("show");
            
            // jQuery event listener voor modal close
            window.jQuery("#mapModal").on('hidden.bs.modal', () => {
              this.handleModalClose();
            });
            
            this.$nextTick(() => {
              this.initFullMap();
            });
            return;
          } else {
            const modalEl = document.getElementById("mapModal");
            if (modalEl) {
              modalEl.style.display = "block";
              modalEl.classList.add("show");
              document.body.classList.add("modal-open");

              const closeButtons = modalEl.querySelectorAll(
                '[data-bs-dismiss="modal"]'
              );
              closeButtons.forEach((button) => {
                button.addEventListener("click", () => {
                  this.handleModalClose();
                  modalEl.style.display = "none";
                  modalEl.classList.remove("show");
                  document.body.classList.remove("modal-open");
                });
              });

              this.$nextTick(() => {
                this.initFullMap();
              });
              return;
            }
          }

          alert("Kon de kaartmodal niet openen.");
          return;
        }
      }

      try {
        this.mapModal.show();
      } catch (error) {
        if (window.jQuery && window.jQuery("#mapModal").modal) {
          window.jQuery("#mapModal").modal("show");
        } else {
          const modalEl = document.getElementById("mapModal");
          if (modalEl) {
            modalEl.style.display = "block";
            modalEl.classList.add("show");
            document.body.classList.add("modal-open");
          }
        }
      }

      this.$nextTick(() => {
        this.initFullMap();
      });
    },

    async searchAddressInModal() {
      if (!this.modalAddressInput.trim()) return;

      if (!window.google?.maps) {
        alert("Google Maps is nog niet geladen.");
        return;
      }

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: this.modalAddressInput }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const result = results[0];
          this.tempLocation = {
            lat: parseFloat(result.geometry.location.lat().toFixed(7)),
            lng: parseFloat(result.geometry.location.lng().toFixed(7)),
            address: result.formatted_address,
            components: this.parseAddressComponents(result.address_components)
          };

          if (this.fullMap) {
            this.fullMap.setCenter(this.tempLocation);
          }

          if (this.fullMarker) {
            this.fullMarker.setPosition(this.tempLocation);
          }
        } else {
          alert("Geen locatie gevonden voor dit adres.");
        }
      });
    },
    confirmLocation() {
      try {
        if (
          !this.tempLocation ||
          typeof this.tempLocation.lat !== "number" ||
          typeof this.tempLocation.lng !== "number"
        ) {
          throw new Error("Ongeldige locatiegegevens");
        }

        const lat = parseFloat(Number(this.tempLocation.lat).toFixed(7));
        const lng = parseFloat(Number(this.tempLocation.lng).toFixed(7));

        if (isNaN(lat) || isNaN(lng)) {
          throw new Error("Ongeldige locatiegegevens (NaN)");
        }

        // Als er geen components zijn, probeer ze op te halen via reverse geocoding
        let components = this.tempLocation.components || {};
        
        if (Object.keys(components).length === 0) {
          // Trigger reverse geocoding voor components als ze ontbreken
          this.performReverseGeocodeSync(lat, lng).then(result => {
            if (result) {
              components = result;
              this.locationData.components = components;
              // Emit components na het ophalen
              if (this.element.mapToFields) {
                this.emitAddressComponents();
              }
            }
          });
        }

        this.locationData = {
          lat: lat,
          lng: lng,
          address: this.tempLocation.address || '',
          components: components
        };

        this.hasLocation = true;
        this.isLocationConfirmed = true;

        try {
          if (this.mapModal) {
            this.mapModal.hide();
          } else {
            const modalEl = document.getElementById("mapModal");
            if (modalEl) {
              modalEl.style.display = "none";
              modalEl.classList.remove("show");
              document.body.classList.remove("modal-open");
            }

            if (window.jQuery && window.jQuery("#mapModal").modal) {
              window.jQuery("#mapModal").modal("hide");
            }
          }
        } catch (error) {
          const modalEl = document.getElementById("mapModal");
          if (modalEl) {
            modalEl.style.display = "none";
            modalEl.classList.remove("show");
            document.body.classList.remove("modal-open");
          }
        }

        setTimeout(() => {
          try {
            this.updateMiniMap();
            this.elementChanged();
          } catch (err) {
            // Stil afhandelen
          }
        }, 300);
      } catch (error) {
        alert("Er is een probleem opgetreden bij het bevestigen van de locatie.");
      }
    },
    clearLocation() {
      this.locationData = { lat: 52.379189, lng: 4.899431, address: "", components: {} };
      this.hasLocation = false;

      if (this.miniMap && this.miniMarker) {
        try {
          this.miniMarker.map = null;
          this.miniMarker = null;
        } catch (error) {
          // Stil afhandelen
        }
      }

      this.elementChanged();
    },
    elementChanged() {
      // Emit complete geo data naar eigen veld (single source of truth)
      this.$emit(
        "elementChanged",
        this.element.name,
        this.hasLocation ? this.locationData : null
      );

      if (this.element.mapToFields && this.hasLocation) {
        // LEGACY: Backwards compatibility voor bestaande lat/lng mapping
        if (this.element.latField && this.element.lngField) {
          const latFieldExists = this.formData && (this.element.latField in this.formData);
          const lngFieldExists = this.formData && (this.element.lngField in this.formData);
          
          if (latFieldExists && lngFieldExists) {
            this.$emit("elementChanged", this.element.latField, this.locationData.lat);
            this.$emit("elementChanged", this.element.lngField, this.locationData.lng);
          }
        }

        // NEW: Eenrichtingsverkeer naar adrescomponent velden
        if (this.locationData.components) {
          this.emitAddressComponents();
        }
      }

      // Detecteer conflict na wijziging
      this.detectLocationConflict();

      const validationElement = this.$refs["validationField_" + this.element.name];
      
      if (validationElement) {
        let customMessage = "";
        if (this.element.required && !this.hasLocation) {
          customMessage = "Selecteer een locatie op de kaart";
        } else if (this.element.required && !this.isLocationConfirmed) {
          customMessage = "Bevestig de locatie in de kaart modal";
        } else if (this.hasLocationConflict) {
          customMessage = "Locatie conflict: geo component en gekoppelde velden verschillen";
        }
        
        validationElement.setCustomValidity(customMessage);
      }

      if (this.wasValidated) {
        this.isInputInvalid = (this.element.required && (!this.hasLocation || !this.isLocationConfirmed)) || this.hasLocationConflict;
        this.invalidStyle = {
          display: this.isInputInvalid ? "block" : "none",
        };
      }
    },

    cancelMapModal() {
      // Gebruik de gemeenschappelijke close handler
      this.handleModalClose();
      
      // Sluit modal handmatig
      try {
        if (this.mapModal) {
          this.mapModal.hide();
        } else {
          const modalEl = document.getElementById("mapModal");
          if (modalEl) {
            modalEl.style.display = "none";
            modalEl.classList.remove("show");
            document.body.classList.remove("modal-open");
          }

          if (window.jQuery && window.jQuery("#mapModal").modal) {
            window.jQuery("#mapModal").modal("hide");
          }
        }
      } catch (error) {
        const modalEl = document.getElementById("mapModal");
        if (modalEl) {
          modalEl.style.display = "none";
          modalEl.classList.remove("show");
          document.body.classList.remove("modal-open");
        }
      }
    },

    handleModalClose() {
      // Herstel originele positie alleen als er daadwerkelijk wijzigingen waren
      const hasChanges = Math.abs(this.tempLocation.lat - this.locationData.lat) > 0.0000001 ||
                         Math.abs(this.tempLocation.lng - this.locationData.lng) > 0.0000001;
      
      if (hasChanges) {
        this.tempLocation = {
          lat: this.locationData.lat,
          lng: this.locationData.lng,
          address: this.locationData.address || ''
        };
        
        // Update kaart en markers naar originele positie
        if (this.fullMap && this.fullMarker) {
          this.fullMap.setCenter(this.tempLocation);
          this.fullMarker.setPosition(this.tempLocation);
        }
        
        // Reset modal address input
        this.modalAddressInput = this.locationData.address || '';
      }
      
      // Reset conflict detectie naar originele staat
      this.detectLocationConflict();
      
      // Cleanup markers
      if (this.fullMarker) {
        try {
          this.fullMarker.setMap(null);
          this.fullMarker = null;
        } catch (e) {
          // Stil afhandelen
        }
      }

      if (this.mappedFieldsMarker) {
        try {
          this.mappedFieldsMarker.setMap(null);
          this.mappedFieldsMarker = null;
        } catch (e) {
          // Stil afhandelen
        }
      }
    },

    // Autocomplete methods
    async initAutocompleteService() {
      if (!this.autocompleteService && window.google?.maps) {
        try {
          // Probeer de nieuwe Places API
          const { Place } = await google.maps.importLibrary("places");
          this.autocompleteService = { Place };
        } catch (error) {
          // Fallback naar legacy API als nieuwe niet beschikbaar is
          if (google.maps.places?.AutocompleteService) {
            this.autocompleteService = new google.maps.places.AutocompleteService();
          }
        }
      }
    },

    onModalAddressInput() {
      this.searchAutocompleteSuggestions(this.modalAddressInput);
    },

    handleModalEnter(event) {
      // Check of er een dropdown item geselecteerd is
      if (this.showModalDropdown && this.modalSelectedIndex >= 0 && this.modalSuggestions[this.modalSelectedIndex]) {
        event.preventDefault();
        this.selectModalSuggestion(this.modalSuggestions[this.modalSelectedIndex]);
      } else if (this.modalAddressInput.trim()) {
        // Geen selectie maar wel tekst, doe gewone zoekopdracht
        event.preventDefault(); // Voorkom modal sluiten
        this.searchAddressInModal();
      } else {
        // Geen tekst en geen selectie, voorkom modal sluiten
        event.preventDefault();
      }
    },

    searchAutocompleteSuggestions(query) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      if (!query || query.length < 2) {
        this.modalSuggestions = [];
        this.showModalDropdown = false;
        return;
      }

      this.searchTimeout = setTimeout(async () => {
        await this.initAutocompleteService();
        
        if (this.autocompleteService) {
          // Check of we de nieuwe Places API hebben
          if (this.autocompleteService.Place) {
            try {
                             // Gebruik de nieuwe Places API Text Search
               const request = {
                 textQuery: query,
                 fields: ['displayName', 'location', 'id', 'formattedAddress', 'addressComponents'],
                 language: 'nl',
                 maxResultCount: 5,
                 locationBias: {
                   center: { lat: 52.3676, lng: 4.9041 }, // Amsterdam centrum
                   radius: 50000 // 50km radius
                 }
               };

               const { places } = await this.autocompleteService.Place.searchByText(request);
               
                                if (places && places.length > 0) {
                 // Converteer naar oude format voor compatibility
                  const predictions = places.map(place => {
                    // Extraheer stad/plaats uit de address components of formatted address
                    let cityName = '';
                    let streetName = place.displayName;
                    
                    if (place.addressComponents) {
                      // Zoek naar locality (stad) in address components
                      const city = place.addressComponents.find(comp => 
                        comp.types.includes('locality') || comp.types.includes('administrative_area_level_2')
                      );
                      if (city) cityName = city.longText;
                    }
                    
                    // Als geen city gevonden, probeer uit formatted address te halen
                    if (!cityName && place.formattedAddress) {
                      const parts = place.formattedAddress.split(',').map(p => p.trim());
                      if (parts.length > 1) {
                        // Vaak is de stad de laatste of voorlaatste component (voor het land)
                        cityName = parts[parts.length - 2] || parts[parts.length - 1];
                        // Filter out postcodes en landen
                        if (cityName && (/^\d{4}\s*[A-Z]{2}/.test(cityName) || cityName === 'Nederland' || cityName === 'Netherlands')) {
                          cityName = parts[parts.length - 3] || '';
                        }
                      }
                    }

                    return {
                      place_id: place.id,
                      description: place.formattedAddress || place.displayName,
                      fullAddress: place.formattedAddress || place.displayName,
                      structured_formatting: {
                        main_text: streetName,
                        secondary_text: cityName
                      }
                    };
                  });

                 this.modalSuggestions = predictions;
                 this.showModalDropdown = true;
                 this.modalSelectedIndex = -1;
               } else {
                 this.modalSuggestions = [];
                 this.showModalDropdown = false;
               }
                         } catch (error) {
               // Fallback naar gewone geocoding als nieuwe API faalt
               this.fallbackToGeocoding(query);
             }
          } else if (this.autocompleteService.getPlacePredictions) {
            // Legacy AutocompleteService
            this.autocompleteService.getPlacePredictions(
              {
                input: query,
                types: ['establishment', 'geocode'],
              },
              (predictions, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
                  // Voeg fullAddress toe aan legacy predictions
                  const enhancedPredictions = predictions.slice(0, 5).map(pred => ({
                    ...pred,
                    fullAddress: pred.description
                  }));
                  this.modalSuggestions = enhancedPredictions;
                  this.showModalDropdown = true;
                  this.modalSelectedIndex = -1;
                } else {
                  this.modalSuggestions = [];
                  this.showModalDropdown = false;
                }
              }
            );
                     } else {
             // Als geen autocomplete beschikbaar is, fallback naar geocoding
             this.fallbackToGeocoding(query);
           }
        }
      }, 300);
    },

    fallbackToGeocoding(query) {
      // Gebruik gewone geocoding als fallback
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ 
        address: query,
        region: 'NL' // Beperk tot Nederland
      }, (results, status) => {
                 if (status === 'OK' && results && results.length > 0) {
           // Converteer geocoding results naar autocomplete format
           const predictions = results.slice(0, 5).map((result, index) => {
             const parts = result.formatted_address.split(',').map(p => p.trim());
             
             // Eerste deel is meestal straat + huisnummer
             const mainText = parts[0] || result.formatted_address;
             
             // Zoek naar de stad (meestal voor de postcode)
             let cityText = '';
             if (parts.length > 1) {
               // Filter uit postcode/land delen en neem de stad
               const filteredParts = parts.slice(1).filter(part => {
                 // Exclusief postcode patterns en landen
                 return !(/^\d{4}\s*[A-Z]{2}/.test(part)) && 
                        part !== 'Nederland' && 
                        part !== 'Netherlands';
               });
               cityText = filteredParts[0] || parts[1];
             }

                          return {
               place_id: `geocode_${index}_${Date.now()}`,
               description: result.formatted_address,
               fullAddress: result.formatted_address,
               structured_formatting: {
                 main_text: mainText,
                 secondary_text: cityText
               },
               geometry: {
                 location: {
                   lat: () => parseFloat(result.geometry.location.lat().toFixed(7)),
                   lng: () => parseFloat(result.geometry.location.lng().toFixed(7))
                 }
               }
             };
           });

           this.modalSuggestions = predictions;
           this.showModalDropdown = true;
           this.modalSelectedIndex = -1;
        }
      });
    },

    navigateDropdown(direction) {
      if (!this.modalSuggestions.length) return;

      const newIndex = Math.max(-1, Math.min(this.modalSuggestions.length - 1, this.modalSelectedIndex + direction));
      this.modalSelectedIndex = newIndex;
      
      // Update input field met geselecteerde tekst voor preview
      if (newIndex >= 0 && this.modalSuggestions[newIndex]) {
        this.modalAddressInput = this.modalSuggestions[newIndex].description;
      }
    },

    selectModalSuggestion(suggestion) {
      this.modalAddressInput = suggestion.description;
      this.hideModalDropdown();
      
      // Check of het een geocoding result is met direct geometry
      if (suggestion.geometry) {
        const location = {
          lat: parseFloat(suggestion.geometry.location.lat().toFixed(7)),
          lng: parseFloat(suggestion.geometry.location.lng().toFixed(7)),
          address: suggestion.fullAddress || suggestion.description
        };
        this.tempLocation = location;
        if (this.fullMap) {
          this.fullMap.setCenter(location);
        }
        if (this.fullMarker) {
          this.fullMarker.setPosition(location);
        }
      } else {
        this.geocodePlaceId(suggestion.place_id, true, suggestion.fullAddress || suggestion.description);
      }
    },

    hideModalDropdown() {
      this.showModalDropdown = false;
      this.modalSelectedIndex = -1;
    },

    hideModalDropdownDelayed() {
      setTimeout(() => this.hideModalDropdown(), 150);
    },

    geocodePlaceId(placeId, isModal = false, fullAddress = '') {
      if (!window.google?.maps) return;

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ placeId: placeId }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = {
            lat: parseFloat(results[0].geometry.location.lat().toFixed(7)),
            lng: parseFloat(results[0].geometry.location.lng().toFixed(7)),
            address: fullAddress || results[0].formatted_address
          };

          if (isModal) {
            this.tempLocation = location;
            if (this.fullMap) {
              this.fullMap.setCenter(location);
            }
            if (this.fullMarker) {
              this.fullMarker.setPosition(location);
            }
          } else {
            this.locationData = location;
            this.hasLocation = true;
            this.isLocationConfirmed = false;
            this.updateMiniMap();
            this.elementChanged();
          }
        } else {
          alert('Locatie niet gevonden');
        }
      });
    },

    reverseGeocode() {
      if (!window.google?.maps || !this.tempLocation.lat || !this.tempLocation.lng) return;
      
      // Debounce om te voorkomen dat er te veel API calls worden gemaakt
      clearTimeout(this.reverseGeocodeTimeout);
      this.reverseGeocodeTimeout = setTimeout(() => {
        const geocoder = new google.maps.Geocoder();
        const latlng = {
          lat: parseFloat(parseFloat(this.tempLocation.lat).toFixed(7)),
          lng: parseFloat(parseFloat(this.tempLocation.lng).toFixed(7))
        };

        geocoder.geocode({ location: latlng }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const result = results[0];
            const address = result.formatted_address;
            this.modalAddressInput = address;
            this.tempLocation.address = address;
            
            // Parse address components voor nieuwe architectuur
            this.tempLocation.components = this.parseAddressComponents(result.address_components);
            
            // Update tempLocation met afgeronde waarden
            this.tempLocation.lat = latlng.lat;
            this.tempLocation.lng = latlng.lng;

            // Update marker positie als deze nog niet goed staat
            if (this.fullMap && this.fullMarker) {
              this.fullMap.setCenter(latlng);
              this.fullMarker.setPosition(latlng);
            }
          }
        });
      }, 500);
    },

    parseAddressComponents(addressComponents) {
      if (!addressComponents) return {};
      
      const components = {};
      
      addressComponents.forEach(component => {
        const types = component.types;
        
        // Straatnaam
        if (types.includes('route')) {
          components.streetName = component.long_name;
        }
        
        // Huisnummer
        if (types.includes('street_number')) {
          components.streetNumber = component.long_name;
        }
        
        // Postcode
        if (types.includes('postal_code')) {
          components.postalCode = component.long_name;
        }
        
        // Plaats/stad
        if (types.includes('locality')) {
          components.locality = component.long_name;
        }
        
        // Gemeente (administrative_area_level_2 in Nederland)
        if (types.includes('administrative_area_level_2')) {
          components.municipality = component.long_name;
        }
        
        // Provincie (administrative_area_level_1 in Nederland)
        if (types.includes('administrative_area_level_1')) {
          components.province = component.long_name;
        }
        
        // Land
        if (types.includes('country')) {
          components.country = component.long_name;
        }
      });
      
      return components;
    },

    async performReverseGeocodeSync(lat, lng) {
      return new Promise((resolve) => {
        if (!window.google?.maps) {
          resolve(null);
          return;
        }

        const geocoder = new google.maps.Geocoder();
        const latlng = { lat: parseFloat(lat), lng: parseFloat(lng) };
        
        geocoder.geocode({ location: latlng }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const components = this.parseAddressComponents(results[0].address_components);
            resolve(components);
          } else {
            resolve(null);
          }
        });
      });
    },

    emitAddressComponents() {
      const components = this.locationData.components;
      if (!components) return;
      
      if (Object.keys(components).length === 0) return;
      
      // Check welke velden geconfigureerd zijn en emit alleen die
      const fieldMappings = [
        { elementField: 'streetNameField', componentKey: 'streetName' },
        { elementField: 'streetNumberField', componentKey: 'streetNumber' },
        { elementField: 'postalCodeField', componentKey: 'postalCode' },
        { elementField: 'localityField', componentKey: 'locality' },
        { elementField: 'municipalityField', componentKey: 'municipality' },
        { elementField: 'provinceField', componentKey: 'province' },
        { elementField: 'countryField', componentKey: 'country' },
        { elementField: 'formattedAddressField', componentKey: 'formattedAddress' }
      ];
      
      fieldMappings.forEach(mapping => {
        const targetField = this.element[mapping.elementField];
        
        if (targetField && this.formData && (targetField in this.formData)) {
          let value = components[mapping.componentKey];
          
          // Speciale behandeling voor formattedAddress
          if (mapping.componentKey === 'formattedAddress') {
            value = this.locationData.address;
          }
          
          if (value) {
            this.$emit("elementChanged", targetField, value);
          }
        }
      });
    },

    // Synchronisatie met gekoppelde velden
    syncWithMappedFields() {
      if (!this.element.mapToFields || !this.formData) return;
      
      const latField = this.element.latField;
      const lngField = this.element.lngField;
      
      if (latField && lngField && this.formData[latField] && this.formData[lngField]) {
        const mappedLat = parseFloat(this.formData[latField]);
        const mappedLng = parseFloat(this.formData[lngField]);
        
        if (!isNaN(mappedLat) && !isNaN(mappedLng)) {
          this.mappedFieldsLocation = {
            lat: mappedLat,
            lng: mappedLng
          };
          
          // Als geo component nog geen locatie heeft, sync met mapped fields
          if (!this.hasLocation) {
            this.locationData = {
              lat: mappedLat,
              lng: mappedLng,
              address: ''
            };
            this.hasLocation = true;
            this.isLocationConfirmed = false;
            this.updateMiniMap();
          }
          
          // Detecteer conflict
          this.detectLocationConflict();
          
          // Update kaarten als modal open is
          if (this.fullMap) {
            this.updateFullMapMarkers();
          }
        }
      } else {
        // Reset conflict als er geen mapping is
        this.hasLocationConflict = false;
        this.mappedFieldsLocation = null;
      }
    },

    detectLocationConflict() {
      if (!this.element.mapToFields || !this.mappedFieldsLocation) {
        this.hasLocationConflict = false;
        return;
      }
      
      // Gebruik tempLocation als modal open is, anders locationData
      const currentLocation = this.fullMap ? this.tempLocation : this.locationData;
      
      // Alleen detecteren als we een locatie hebben
      if (!currentLocation || (!this.hasLocation && !this.fullMap)) {
        this.hasLocationConflict = false;
        return;
      }
      
      const threshold = 0.0001; // ~11 meter verschil
      const latDiff = Math.abs(currentLocation.lat - this.mappedFieldsLocation.lat);
      const lngDiff = Math.abs(currentLocation.lng - this.mappedFieldsLocation.lng);
      
      this.hasLocationConflict = latDiff > threshold || lngDiff > threshold;
      
      // Update validatie status bij conflict (alleen voor hoofd component, niet modal)
      if (this.hasLocationConflict && this.wasValidated && !this.fullMap) {
        this.isInputInvalid = true;
        this.invalidStyle = {
          display: "block",
        };
        
        const validationElement = this.$refs["validationField_" + this.element.name];
        if (validationElement) {
          validationElement.setCustomValidity("Locatie conflict: geo component en gekoppelde velden verschillen");
        }
      }
    },

    updateFullMapMarkers() {
      if (!this.fullMap) return;
      
      // Check eerst of beide markers verschillende posities hebben
      const hasGeoLocation = this.hasLocation && this.locationData.lat && this.locationData.lng;
      const hasMappedLocation = this.mappedFieldsLocation && this.mappedFieldsLocation.lat && this.mappedFieldsLocation.lng;
      
      let sameLocation = true;
      if (hasGeoLocation && hasMappedLocation) {
        sameLocation = Math.abs(this.locationData.lat - this.mappedFieldsLocation.lat) < 0.0001 &&
                       Math.abs(this.locationData.lng - this.mappedFieldsLocation.lng) < 0.0001;
      }
      
      // Verwijder bestaande mapped fields marker
      if (this.mappedFieldsMarker) {
        this.mappedFieldsMarker.setMap(null);
        this.mappedFieldsMarker = null;
      }
      
      // Toon rode marker ALLEEN als er een conflict is (verschillende locaties)
      if (hasMappedLocation && !sameLocation) {
        this.mappedFieldsMarker = new google.maps.Marker({
          position: this.mappedFieldsLocation,
          map: this.fullMap,
          title: "Locatie uit gekoppelde velden (conflict!)",
          icon: {
            url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(this.redMarkerSvg),
            size: new google.maps.Size(32, 44),
            anchor: new google.maps.Point(16, 39),
            scaledSize: new google.maps.Size(32, 44),
          },
        });
        
        // Zoom uit om beide markers te zien
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(this.locationData);
        bounds.extend(this.mappedFieldsLocation);
        this.fullMap.fitBounds(bounds);
        
        // Voeg wat padding toe
        setTimeout(() => {
          this.fullMap.setZoom(Math.max(this.fullMap.getZoom() - 1, 8));
        }, 100);
      }
    },

    // Detecteer of er een record wissel heeft plaatsgevonden
    detectRecordSwitch(newVal, oldVal) {
      // Als er geen oldVal is (eerste keer), geen switch
      if (!oldVal) return false;
      
      // Als we naar een nieuw record gaan (__isNew), reset altijd
      if (newVal && newVal.__isNew === true) {
        return this.hasLocation; // Alleen reset als er geo data was
      }
      
      // Vergelijk record IDs als beide aanwezig zijn
      if (newVal && newVal.id && oldVal && oldVal.id) {
        return newVal.id !== oldVal.id;
      }
      
      // Vergelijk op basis van andere velden om record switch te detecteren
      // Kijk naar een paar common velden die vaak verschillen tussen records
      const compareFields = ['email', 'naam', 'voornaam', 'achternaam', 'name', '_createdAt', '_updatedAt'];
      
      for (const field of compareFields) {
        const newFieldValue = this.getFieldValue(newVal, field);
        const oldFieldValue = this.getFieldValue(oldVal, field);
        
        // Als beide velden bestaan en verschillen, is het een andere record
        if (newFieldValue && oldFieldValue && newFieldValue !== oldFieldValue) {
          return true;
        }
        
        // Als een van de velden bestaat in de ene maar niet in de andere
        if ((newFieldValue && !oldFieldValue) || (!newFieldValue && oldFieldValue)) {
          return true;
        }
      }
      
      return false;
    },

    // Helper om veldwaarde op te halen (ook uit objecten)
    getFieldValue(record, fieldName) {
      if (!record || !record[fieldName]) return null;
      
      const fieldValue = record[fieldName];
      
      // Als het een object is met een value property
      if (typeof fieldValue === 'object' && fieldValue.value !== undefined) {
        return fieldValue.value;
      }
      
      // Anders de waarde direct
      return fieldValue;
    },

    // Reset geo component state bij record wissel
    resetGeoState() {
      console.log('AFormElementGeo: Resetting geo state');
      
      // Reset locatie data
      this.locationData = { lat: 52.379189, lng: 4.899431, address: "", components: {} };
      this.tempLocation = { lat: 52.379189, lng: 4.899431, address: "", components: {} };
      this.hasLocation = false;
      this.isLocationConfirmed = false;
      this.mappedFieldsLocation = null;
      this.hasLocationConflict = false;
      
      // Reset input velden
      this.modalAddressInput = "";
      
      // Reset markers
      if (this.miniMarker) {
        this.miniMarker.setMap(null);
        this.miniMarker = null;
      }
      
      if (this.fullMarker) {
        this.fullMarker.setMap(null);
        this.fullMarker = null;
      }
      
      if (this.mappedFieldsMarker) {
        this.mappedFieldsMarker.setMap(null);
        this.mappedFieldsMarker = null;
      }
      
      // Reset validatie
      const validationElement = this.$refs["validationField_" + this.element.name];
      if (validationElement) {
        if (this.element.required) {
          validationElement.setCustomValidity("Selecteer een locatie op de kaart");
        } else {
          validationElement.setCustomValidity("");
        }
      }
      
      // Emit change event om parent te informeren
      this.elementChanged();
    },
  },
  mounted() {
    if (this.formData && this.formData[this.element.name]) {
      const geoData = this.formData[this.element.name];
      if (geoData && geoData.lat && geoData.lng) {
        this.locationData = {
          lat: parseFloat(geoData.lat),
          lng: parseFloat(geoData.lng),
          address: geoData.address || '',
        };
        this.hasLocation = true;
        this.isLocationConfirmed = true; // Data uit formData is al bevestigd

        setTimeout(() => {
          this.updateMiniMap();
        }, 300);
      }
    }

    const validationElement = this.$refs["validationField_" + this.element.name];
    if (validationElement && this.element.required) {
      if (!this.hasLocation) {
        validationElement.setCustomValidity("Selecteer een locatie op de kaart");
      } else if (!this.isLocationConfirmed) {
        validationElement.setCustomValidity("Bevestig de locatie in de kaart modal");
      } else {
        validationElement.setCustomValidity("");
      }
    }

    // Synchroniseer met gekoppelde velden als mapToFields is ingeschakeld
    if (this.element.mapToFields) {
      this.syncWithMappedFields();
    }
  },
};
</script>

<style scoped>
/* Bij 320px breed (400% zoom) knijpt de knop het adresveld tot ~90px, waarna
   een gekozen adres onleesbaar wordt afgekapt. Onder die breedte krijgt de knop
   een eigen regel; de rechte hoeken van de input-group moeten dan wel terug,
   anders houden beide helften hun halve afronding. */
@media (max-width: 400px) {
  .geo-invoer .form-control.geo-clickable-input {
    min-width: 100%;
    border-radius: 0.375rem;
    margin-bottom: 0.5rem;
  }

  .geo-invoer .btn {
    border-radius: 0.375rem;
  }
}

/* Buiten beeld maar niet display:none of visibility:hidden — die sluiten een
   veld uit van constraint-validatie, en juist daar ging het mis. */
.geo-validatie {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

.geo-preview-container {
  position: relative;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.geo-mini-map {
  height: 150px;
  width: 100%;
}

.geo-full-map {
  height: 400px;
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.geo-coordinates {
  background: rgba(255, 255, 255, 0.9);
  padding: 5px 10px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  font-size: 0.875rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  opacity: 0;
  transform: translateY(0);
  transition: opacity 0.6s ease, transform 0.6s ease;
  backdrop-filter: blur(2px);
}

.geo-preview-container:hover .geo-coordinates {
  opacity: 1;
}

.autocomplete-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 1050;
  max-height: 200px;
  overflow-y: auto;
}

.autocomplete-item {
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.15s ease;
}

.autocomplete-item:hover,
.autocomplete-item.active {
  background-color: #f8f9fa;
}

.autocomplete-item:last-child {
  border-bottom: none;
}

.autocomplete-item strong {
  color: #333;
  font-weight: 500;
}

.autocomplete-item .text-muted {
  color: #6c757d !important;
  margin-top: 2px;
}

.geo-clickable-input {
  cursor: pointer;
}

.geo-clickable-input:hover {
  background-color: #f8f9fa;
}

/* Conflict warning styling - alleen voor individual form controls */
.geo-preview-container.conflict-border {
  border: 2px solid #dc3545 !important;
  border-radius: 6px !important;
}

.geo-clickable-input:hover {
  background-color: #f8f9fa;
}
</style>
